import { useSyncExternalStore } from 'react'

export type WorkspaceArtifactEventInput = {
  projectId: number
  kind: 'proposal' | 'presentation' | 'document'
  proposalId?: number
  sectionId?: number
  presentationId?: number
  slideId?: number
  documentId?: number
}

export type WorkspaceArtifactEvent = WorkspaceArtifactEventInput & { sequence: number }

// One workspace per browser tab, so module-level state stands in for a store library.
// Sequences are assigned here rather than reused from the SSE eventId because those are
// per-conversation and collide across chats; staleness needs one order across the whole
// workspace that never resets, even when old events fall out of retention.
const RETAINED_EVENT_LIMIT = 200

let retainedEvents: Array<WorkspaceArtifactEvent> = []
let lastAssignedSequence = 0
const listeners = new Set<() => void>()

export function publishArtifactEvents(events: Array<WorkspaceArtifactEventInput>): void {
  if (events.length === 0) return
  for (const event of events) {
    lastAssignedSequence += 1
    retainedEvents.push({ ...event, sequence: lastAssignedSequence })
  }
  if (retainedEvents.length > RETAINED_EVENT_LIMIT) {
    retainedEvents = retainedEvents.slice(retainedEvents.length - RETAINED_EVENT_LIMIT)
  }
  // Notifying after the whole batch lets each listener reconcile once against complete state.
  for (const listener of [...listeners]) listener()
}

export function subscribeArtifactChanges(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function highestMatchingSequence(projectId: number, matches: (event: WorkspaceArtifactEvent) => boolean): number {
  let revision = 0
  for (const event of retainedEvents) {
    if (event.projectId === projectId && matches(event)) revision = event.sequence
  }
  return revision
}

export function useArtifactRevision(projectId: number, matches: (event: WorkspaceArtifactEvent) => boolean): number {
  // The snapshot is a plain number, so a publication that matches nothing keeps React from
  // re-rendering; only a newly matching event changes the value.
  // matches runs on every render and every publication, so it must stay pure; side effects
  // break under StrictMode double invokes.
  return useSyncExternalStore(subscribeArtifactChanges, () => highestMatchingSequence(projectId, matches))
}

// Test isolation only: empties the store and restarts sequences without notifying anyone,
// so assertions stay tied to real publications.
export function resetArtifactChangesForTests(): void {
  retainedEvents = []
  lastAssignedSequence = 0
}
